import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useDebounce from '../../hooks/useDebounce';
import { Edit2, Trash2, Eye, ChevronLeft, ChevronRight, Calendar, List, LayoutGrid, PhoneCall, X, CheckCircle, Zap, Activity } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AddLead from './AddLead';
import TakeActionModal from './TakeActionModal';
import LeadPipeline from './components/LeadPipeline';
import Dropdown from '../../components/Dropdown';
import DateFilter from '../../components/DateFilter';
import PrinterLoader from '../../components/PrinterLoader';
import { isDateInRange, formatAppDate, formatAppDateOnly } from '../../utils/dateUtils';
import { sortStatuses, getStatusColors } from '../../utils/statusUtils';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const LeadList = () => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [deleteLeadId, setDeleteLeadId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [selectedLeads, setSelectedLeads] = useState([]);
  
  // Pagination & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('this_month');
  const [followUpDateFilter, setFollowUpDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState(null);
  const [customFollowUpStartDate, setCustomFollowUpStartDate] = useState('');
  const [customFollowUpEndDate, setCustomFollowUpEndDate] = useState(null);
  
  // Follow-up quick action states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpLead, setFollowUpLead] = useState(null);
  const [followUpDate, setFollowUpDate] = useState(null);
  const [followUpTime, setFollowUpTime] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  
  // Take Action states
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionLead, setActionLead] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionOutcome, setActionOutcome] = useState('Completed');
  const [actionDate, setActionDate] = useState(null);
  const [actionTime, setActionTime] = useState('');
  const [actionDaysToAdd, setActionDaysToAdd] = useState('');
  
  const handleActionDaysChange = (val) => {
    setActionDaysToAdd(val);
    if (val && !isNaN(val)) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(val, 10));
      setActionDate(d);
    } else {
      setActionDate(null);
    }
  };

  const handleActionDateChange = (date) => {
    setActionDate(date);
    if (date) {
      const today = new Date();
      today.setHours(0,0,0,0);
      const sel = new Date(date);
      sel.setHours(0,0,0,0);
      const diffTime = Math.abs(sel - today);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setActionDaysToAdd(diffDays.toString());
    } else {
      setActionDaysToAdd('');
    }
  };
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [statusesRes, usersRes, sourcesRes] = await Promise.all([
          api.get('/masters/status'),
          api.get('/auth/users'),
          api.get('/masters/lead-sources')
        ]);
        setStatuses(sortStatuses(statusesRes.data.filter(s => s.status === 'Active')));
        setUsersList(usersRes.data.filter(u => u.status === 'Active'));
        setSourcesList(sourcesRes.data.filter(s => s.status === 'Active'));
      } catch (err) {
        console.error('Failed to fetch filter data', err);
      }
    };
    fetchFilterData();
  }, []);


  useEffect(() => {
    if (location.state?.openLeadId && !loading && leads.length > 0) {
      const leadToOpen = leads.find(l => l.id === location.state.openLeadId);
      if (leadToOpen) {
        setIsViewMode(true);
        setEditingLead(leadToOpen);
        setShowModal(true);
        
        // Clear state so it doesn't reopen on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, loading, leads, navigate]);

  const getTimeDate = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    return d;
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
      // Backend doesn't support status, source, user filters yet, but we'll prepare them
      if (statusFilter) queryParams.append('status', statusFilter);
      if (sourceFilter) queryParams.append('source', sourceFilter);
      if (userFilter) queryParams.append('assignee', userFilter);
      
      const res = await api.get(`/leads?${queryParams.toString()}`);
      
      let dataToFormat = res.data.data || res.data;
      
      const formattedLeads = dataToFormat.map(lead => ({
        id: lead.id,
        name: lead.companyName,
        userId: lead.userId,
        contact: lead.contactPerson,
        phone: lead.phone,
        email: lead.email,
        contactPhone: lead.contactPhone,
        contactEmail: lead.contactEmail,
        frontCard: lead.frontCard,
        backCard: lead.backCard,
        address: lead.address,
        address2: lead.address2,
        city: lead.city,
        state: lead.state,
        pincode: lead.pincode,
        latitude: lead.latitude,
        longitude: lead.longitude,
        agencyType: lead.agencyType,
        machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
        machineBrand: lead.machine?.name || '',
        machineModel: lead.machineModel || '',
        type: lead.interestType || lead.leadType?.name || 'Unknown',
        interestType: lead.interestType || lead.leadType?.name || '',
        otherAgencyType: lead.otherAgencyType || '',
        machineType: lead.machineType || '',
        assignee: lead.user?.name || 'Unassigned',
        status: lead.status?.name || 'Pending',
        source: lead.leadSource?.name || 'Unknown',
        customerType: lead.customerType?.name || null,
        createdAt: lead.createdAt,
        createdBy: lead.createdBy || 'Unknown',
        notes: lead.notes,
        followups: lead.followups || [],
        nextFollowUp: (['complete', 'completed', 'closed', 'lost'].includes((lead.status?.name || lead.status || '').toLowerCase()))
          ? null 
          : (lead.followups && lead.followups.length > 0 
            ? [...lead.followups]
                .filter(f => f.type !== 'System' && !f.notes?.startsWith('[Completed]') && !f.notes?.startsWith('[Rescheduled]') && !f.notes?.startsWith('[Closed]') && !f.notes?.startsWith('[Cancelled]'))
                .sort((a,b) => new Date(b.date) - new Date(a.date))[0]?.date 
            : null)
      }));
      setLeads(formattedLeads);
      
      if (res.data.data) {
        setTotalPages(res.data.totalPages || 1);
        setTotalLeads(res.data.total || 0);
      } else {
        setTotalPages(1);
        setTotalLeads(res.data.length);
      }
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, statusFilter, sourceFilter, userFilter]);

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setIsViewMode(false);
    setShowModal(true);
  };

  const handleView = (lead) => {
    setEditingLead(lead);
    setIsViewMode(true);
    setShowModal(true);
  };
  
  const handleFollowUpClick = (lead) => {
    setFollowUpLead(lead);
    setFollowUpDate(null);
    setFollowUpTime('');
    setFollowUpNotes('');
    setShowFollowUpModal(true);
  };

  const handleActionClick = (lead) => {
    setActionLead(lead);
    setActionOutcome(lead.status?.name || lead.status || statuses[0]?.name || 'Completed');
    setActionDate(null);
    setActionTime('');
    setActionNotes('');
    setShowActionModal(true);
  };

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    if (!followUpLead || !followUpDate || !followUpTime) {
      toast.error("Please select a date and time");
      return;
    }
    try {
      const d = new Date(followUpDate);
      const [hours, minutes] = followUpTime.split(':');
      d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      await api.post('/followups', {
        leadId: followUpLead.id,
        date: d.toISOString(),
        notes: followUpNotes || 'Quick follow-up scheduled',
        type: 'Call'
      });
      toast.success("Follow-up scheduled successfully");
      setShowFollowUpModal(false);
      fetchLeads();
    } catch (err) {
      console.error(err);
      toast.error("Failed to schedule follow-up");
    }
  };
  
  const handleTakeActionClick = (lead) => {
    setActionLead(lead);
    setActionNotes('');
    setActionOutcome('Completed');
    setActionDate(null);
    setActionTime('');
    setShowActionModal(true);
  };

  const handleAdd = () => {
    setEditingLead(null);
    setIsViewMode(false);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteLeadId(id);
  };

  const confirmDelete = async () => {
    if (deleteLeadId) {
      try {
        await api.delete(`/leads/${deleteLeadId}`);
        toast.success('Lead deleted successfully');
        setDeleteLeadId(null);
        fetchLeads();
      } catch (error) {
        toast.error('Failed to delete lead');
        console.error(error);
      }
    }
  };

  const handleCloseModal = (shouldRefresh) => {
    setShowModal(false);
    setEditingLead(null);
    setIsViewMode(false);
    if (shouldRefresh === true) {
      fetchLeads();
    }
  };

  // Filter Logic
  const roleFilteredLeads = (user?.role === 'Admin' || hasPermission('leads:read_all')) ? leads : leads.filter(l => l.assignee === user?.name || l.createdBy === user?.name);

  const filteredLeads = roleFilteredLeads.filter(lead => {
    // Only date filtering left locally since others are in backend or partially handled
    const matchesDate = isDateInRange(lead.createdAt, dateFilter, customStartDate, customEndDate);
    const matchesFollowUpDate = isDateInRange(lead.nextFollowUp, followUpDateFilter, customFollowUpStartDate, customFollowUpEndDate);
    return matchesDate && matchesFollowUpDate;
  });

  // Sort Logic (Newest First)
  const paginatedLeads = [...filteredLeads].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(paginatedLeads.map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (id) => {
    setSelectedLeads(prev => 
      prev.includes(id) ? prev.filter(lId => lId !== id) : [...prev, id]
    );
  };

  const handleBulkReassign = () => {
    alert(`Re-assigning leads: ${selectedLeads.join(', ')}`);
    setSelectedLeads([]);
  };

  const handleBulkDelete = () => {
    if(window.confirm('Are you sure you want to delete selected leads?')) {
      alert(`Deleted leads: ${selectedLeads.join(', ')}`);
      setSelectedLeads([]);
    }
  };

  const handleExportCSV = () => {
    if (filteredLeads.length === 0) {
      toast.error('No leads to export');
      return;
    }
    const headers = ['Lead No.', 'Lead Name', 'Contact Person', 'Email', 'Phone', 'Machine Int.', 'Source', 'Assignee', 'Creation Date', 'Next Follow-up', 'Status'];
    const rows = filteredLeads.map(lead => {
      return [
        `#${lead.id}`,
        `"${(lead.companyName || '').replace(/"/g, '""')}"`,
        `"${(lead.contactPerson || '').replace(/"/g, '""')}"`,
        `"${(lead.email || '').replace(/"/g, '""')}"`,
        `"${(lead.phone || '').replace(/"/g, '""')}"`,
        `"${(lead.machine?.name || 'Unknown').replace(/"/g, '""')}"`,
        `"${(lead.leadSource?.name || 'Unknown').replace(/"/g, '""')}"`,
        `"${(lead.user?.name || 'Unassigned').replace(/"/g, '""')}"`,
        `"${formatAppDateOnly(lead.createdAt)}"`,
        `"${lead.nextFollowUp ? formatAppDateOnly(lead.nextFollowUp) : ''}"`,
        `"${(lead.status || 'New').replace(/"/g, '""')}"`
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Leads_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {loading && <PrinterLoader />}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Leads</h2>
          <p className="text-muted mt-2">View and manage all your agency leads.</p>
        </div>
        <div className="flex gap-4 items-center">
          <div style={{ display: 'flex', backgroundColor: '#f1f5f9', padding: '0.25rem', borderRadius: '0.5rem', alignItems: 'center' }}>
            <button 
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                backgroundColor: viewMode === 'list' ? 'white' : 'transparent',
                boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                color: viewMode === 'list' ? 'var(--color-primary)' : '#64748b',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={20} />
            </button>
            <button 
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                backgroundColor: viewMode === 'kanban' ? 'white' : 'transparent',
                boxShadow: viewMode === 'kanban' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                color: viewMode === 'kanban' ? 'var(--color-primary)' : '#64748b',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onClick={() => setViewMode('kanban')}
              title="Pipeline View"
            >
              <LayoutGrid size={20} />
            </button>
          </div>
          {user?.permissions?.includes("leads:write") && (
            <button className="btn btn-primary" onClick={handleAdd}>
            + Add New Lead
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex justify-between items-center gap-4 flex-wrap">
             <input 
               type="text" 
               placeholder="Search leads by name, contact or phone..." 
               className="input-field" 
               style={{ flex: '1', minWidth: '250px', maxWidth: '320px' }}
               value={searchTerm}
               onChange={(e) => {
                 setSearchTerm(e.target.value);
                 setCurrentPage(1); // reset to page 1 on search
               }} 
             />
             <div className="flex gap-4 items-center flex-wrap">
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">Created:</span>
                 <DateFilter 
                   value={dateFilter}
                   onChange={(val) => {
                     setDateFilter(val);
                     setCurrentPage(1);
                   }}
                 />
               </div>
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">Follow-up:</span>
                 <DateFilter 
                   value={followUpDateFilter}
                   onChange={(val) => {
                     setFollowUpDateFilter(val);
                     setCurrentPage(1);
                   }}
                 />
               </div>
               {viewMode !== 'kanban' && (
                 <Dropdown 
                   style={{ minWidth: '150px' }}
                   value={statusFilter}
                   placeholder="All Statuses"
                   onChange={(val) => {
                     setStatusFilter(val);
                     setCurrentPage(1);
                   }}
                   options={[
                     { value: '', label: 'All Statuses' },
                     ...statuses.map(s => ({ value: s.name, label: s.name }))
                   ]}
                 />
               )}
                <Dropdown 
                  style={{ minWidth: '140px' }}
                  value={sourceFilter}
                  placeholder="All Sources"
                  onChange={(val) => {
                    setSourceFilter(val);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: '', label: 'All Sources' },
                    ...sourcesList.map(s => ({ value: s.name, label: s.name }))
                  ]}
                />
                {user?.role === 'Admin' && (
                  <Dropdown 
                    style={{ minWidth: '140px' }}
                    value={userFilter}
                    placeholder="All Users"
                    onChange={(val) => {
                      setUserFilter(val);
                      setCurrentPage(1);
                    }}
                    options={[
                      { value: '', label: 'All Users' },
                      ...usersList.map(u => ({ value: u.name, label: u.name })),
                      { value: 'Unassigned', label: 'Unassigned' },
                    ]}
                  />
                )}
                {user?.role === 'Admin' && (
                  <button className="btn" style={{ border: '1.5px solid var(--color-border)', backgroundColor: 'white' }} onClick={handleExportCSV}>Export</button>
                )}
             </div>
          </div>
          
          {/* Custom Date Range Picker for Created */}
          {dateFilter === 'custom' && (
            <div className="flex justify-end gap-4 fade-in">
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">Created From:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customStartDate}
                   onChange={e => {
                     setCustomStartDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">To:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customEndDate || ''}
                   onChange={e => {
                     setCustomEndDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
            </div>
          )}

          {/* Custom Date Range Picker for Follow-up */}
          {followUpDateFilter === 'custom' && (
            <div className="flex justify-end gap-4 fade-in">
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">Follow-up From:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customFollowUpStartDate}
                   onChange={e => {
                     setCustomFollowUpStartDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
               <div className="flex items-center gap-2">
                 <span className="text-sm font-semibold text-muted">To:</span>
                 <input 
                   type="date" 
                   className="input-field" 
                   value={customFollowUpEndDate || ''}
                   onChange={e => {
                     setCustomFollowUpEndDate(e.target.value);
                     setCurrentPage(1);
                   }}
                 />
               </div>
            </div>
          )}
          
          {/* Bulk Actions Bar */}
          {selectedLeads.length > 0 && (
            <div style={{ backgroundColor: 'var(--color-primary-light)', padding: '0.75rem 1.5rem', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', border: '1px solid var(--color-primary)' }}>
              <div style={{ color: 'var(--color-primary-hover)', fontWeight: '600' }}>
                {selectedLeads.length} Lead(s) Selected
              </div>
              <div className="flex gap-2">
                {user?.role === 'Admin' && (
                  <button className="btn" style={{ backgroundColor: 'var(--color-danger)', color: 'white' }} onClick={handleBulkDelete}>Delete</button>
                )}
              </div>
            </div>
          )}
        </div>

        {viewMode === 'kanban' ? (
          <LeadPipeline leads={filteredLeads} onView={handleView} statuses={statuses} />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                <th style={{ padding: '1rem', width: '5%' }}>Lead No.</th>
                <th style={{ padding: '1rem', width: '15%' }}>Lead Details</th>
                <th style={{ padding: '1rem', width: '13%' }}>Contact</th>
                <th style={{ padding: '1rem', width: '15%' }}>Product Interest</th>
                <th style={{ padding: '1rem', width: '12%' }}>Source</th>
                <th style={{ padding: '1rem', width: '13%' }}>Assignee</th>
                <th style={{ padding: '1rem', width: '10%' }}>Created Date</th>
                <th style={{ padding: '1rem', width: '10%' }}>Next Follow-up</th>
                <th style={{ padding: '1rem', width: '10%' }}>Status</th>
                <th style={{ padding: '1rem', width: '7%', textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.length > 0 ? (
                paginatedLeads.map((lead, index) => (
                  <tr key={lead.id} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: selectedLeads.includes(lead.id) ? 'var(--color-primary-light)' : 'transparent' }}>
                    <td style={{ padding: '1rem' }}>
                      <button 
                        onClick={() => handleView(lead)}
                        style={{ color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontSize: 'inherit' }}
                        title="View Lead Details"
                      >
                        #{lead.id}
                      </button>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div className="flex flex-col items-start gap-1">
                        <span style={{ fontWeight: '500' }}>{lead.name}</span>
                        {lead.customerType && (
                          <span style={{
                            padding: '0.15rem 0.4rem',
                            borderRadius: '4px',
                            backgroundColor: lead.customerType === 'Hot' ? '#ffedd5' : lead.customerType === 'Warm' ? '#fef3c7' : '#e0f2fe',
                            color: lead.customerType === 'Hot' ? '#ea580c' : lead.customerType === 'Warm' ? '#d97706' : '#0369a1',
                            fontSize: '0.65rem',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {lead.customerType}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div>{lead.contact}</div>
                      <div className="text-xs mt-1">
                        <a href={`tel:${lead.phone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{lead.phone}</a>
                      </div>
                      <div className="text-xs mt-1">
                        {lead.email && <a href={`mailto:${lead.email}`} style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>{lead.email}</a>}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {lead.type !== 'Unknown' && <div style={{ fontWeight: '500', color: 'var(--color-text-main)', marginBottom: '0.25rem' }}>{lead.type}</div>}
                      {lead.machineType && <div style={{ fontWeight: '600', color: 'var(--color-primary)', fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.25rem' }}>{lead.machineType === 'rental' ? 'Rental Machine' : lead.machineType === 'new' ? 'New Machine' : lead.machineType}</div>}
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{lead.machine !== 'Unknown' ? lead.machine : ''}</div>
                    </td>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{lead.source}</td>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{lead.assignee}</td>
                    <td style={{ padding: '1rem' }}>{formatAppDateOnly(lead.createdAt)}</td>
                    <td style={{ padding: '1rem' }}>
                      {lead.nextFollowUp 
                        ? formatAppDateOnly(lead.nextFollowUp)
                        : <span className="text-muted">—</span>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.6rem', 
                        borderRadius: '9999px', 
                        backgroundColor: getStatusColors(lead.status).bg,
                        color: getStatusColors(lead.status).text,
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap'
                      }}>
                        
                        {lead.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div className="flex gap-2 justify-center">
                        {!['complete', 'completed', 'not interested', 'closed', 'won', 'lost'].includes(lead.status?.trim().toLowerCase()) && user?.permissions?.includes('followups:write') && (
                          (lead.status?.toLowerCase().includes('schedule') || lead.status?.toLowerCase().includes('follow')) ? (
                            <button className="btn text-primary" style={{ padding: '0.3rem' }} onClick={() => handleTakeActionClick(lead)} title="Take Action">
                              <Activity size={18} />
                            </button>
                          ) : (
                            <button 
                              className="btn text-primary" 
                              style={{ padding: '0.3rem' }} 
                              onClick={() => handleActionClick(lead)} 
                              title="Take Action"
                            >
                              <Activity size={18} />
                            </button>
                          )
                        )}
                        <button className="btn text-primary" style={{ padding: '0.3rem' }} onClick={() => handleView(lead)} title="View">
                          <Eye size={18} />
                        </button>
                          <>
                            {user?.permissions?.includes("leads:write") && (
                            <button className="btn text-muted" style={{ padding: '0.3rem' }} onClick={() => handleEdit(lead)} title="Edit">
                              <Edit2 size={18} />
                            </button>
                          )}
                            {user?.permissions?.includes("leads:delete") && (
                            <button className="btn text-danger" style={{ padding: '0.3rem' }} onClick={() => handleDelete(lead.id)} title="Delete">
                              <Trash2 size={18} color="var(--color-danger)" />
                            </button>
                          )}
                          </>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    No leads found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {filteredLeads.length > 0 && (
          <div className="flex justify-between items-center mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-4 text-sm text-muted">
              <div>
                Showing <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min((currentPage - 1) * itemsPerPage + 1, filteredLeads.length)}</span> to <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min(currentPage * itemsPerPage, filteredLeads.length)}</span> of <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{filteredLeads.length}</span> entries (filtered)
              </div>
              <div className="flex items-center gap-2">
                <span>Show:</span>
                <Dropdown 
                  style={{ width: '80px', height: '32px' }}
                  value={itemsPerPage}
                  onChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                className="btn" 
                style={{ padding: '0.5rem', border: '1px solid var(--color-border)', backgroundColor: currentPage === 1 ? '#f8fafc' : 'white', opacity: currentPage === 1 ? 0.5 : 1 }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              
              <div className="flex items-center gap-1 mx-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button 
                    key={i} 
                    className="btn"
                    style={{ 
                      padding: '0.25rem 0.75rem', 
                      backgroundColor: currentPage === i + 1 ? 'var(--color-primary)' : 'transparent',
                      color: currentPage === i + 1 ? 'white' : 'var(--color-text-main)',
                      fontWeight: currentPage === i + 1 ? '600' : '400'
                    }}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button 
                className="btn" 
                style={{ padding: '0.5rem', border: '1px solid var(--color-border)', backgroundColor: currentPage === totalPages ? '#f8fafc' : 'white', opacity: currentPage === totalPages ? 0.5 : 1 }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 50,
          padding: '2rem'
        }}>
          <div className="hide-scrollbar" style={{
            backgroundColor: 'var(--color-bg-card)',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh', overflow: 'hidden',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <AddLead 
              onClose={handleCloseModal} 
              initialData={editingLead} 
              isViewMode={isViewMode} 
              onEdit={(lead) => {
                setShowModal(false);
                setTimeout(() => handleEdit(lead), 0);
              }}
              onDelete={(id) => {
                handleDelete(id);
                // Note: handleDelete handles confirmation and its own fetch/state refresh
                setShowModal(false);
              }}
              onAction={(lead) => {
                // DO NOT close the lead profile modal!
                handleTakeActionClick(lead);
              }}
            />
          </div>
        </div>
      )}
      {showActionModal && actionLead && (
        <TakeActionModal 
          lead={actionLead} 
          onClose={() => setShowActionModal(false)}
          onSuccess={async () => {
            setShowActionModal(false);
            fetchLeads();
            if (showModal && editingLead && editingLead.id === actionLead.id) {
              try {
                const res = await api.get(`/leads/${editingLead.id}`);
                setEditingLead(res.data);
              } catch (e) {
                console.error("Failed to refresh lead data", e);
              }
            }
          }} 
        />
      )}

      {showFollowUpModal && (
        <div className="modal-backdrop">
          <div className="modal-content fade-in" style={{ maxWidth: '500px', padding: '2rem' }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-main">Quick Follow-up</h3>
              <button className="btn btn-secondary p-2" onClick={() => setShowFollowUpModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleFollowUpSubmit}>
              <div className="mb-6">
                <label className="text-sm font-semibold mb-2 block text-muted">Date & Time *</label>
                <DatePicker
                  selected={followUpDate}
                  onChange={(date) => {
                    setFollowUpDate(date);
                    if (date) {
                      const hours = String(date.getHours()).padStart(2, '0');
                      const mins = String(date.getMinutes()).padStart(2, '0');
                      setFollowUpTime(`${hours}:${mins}`);
                    } else {
                      setFollowUpTime('');
                    }
                  }}
                  className="input-field w-full"
                  dateFormat="dd-MMM-yyyy hh:mm aa"
                  placeholderText="Select Date & Time"
                  minDate={new Date()}
                  showTimeSelect
                  timeFormat="hh:mm aa"
                  timeIntervals={15}
                  required
                  style={{ borderRadius: '12px' }}
                  minTime={
                    followUpDate && new Date(followUpDate).setHours(0,0,0,0) === new Date().setHours(0,0,0,0)
                      ? new Date()
                      : new Date(new Date().setHours(0, 0, 0, 0))
                  }
                  maxTime={new Date(new Date().setHours(23, 59, 59, 999))}
                />
              </div>
              <div className="mb-6">
                <label className="text-sm font-semibold mb-2 block text-muted">Notes</label>
                <textarea 
                  className="input-field w-full" 
                  rows="3" 
                  placeholder="E.g., Call again next week..."
                  value={followUpNotes}
                  onChange={e => setFollowUpNotes(e.target.value)}
                  style={{ resize: 'none', borderRadius: '12px' }}
                ></textarea>
              </div>
              <div className="flex gap-4 justify-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowFollowUpModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#10b981', color: 'white' }}>
                  Schedule Follow-up
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteLeadId && (
        <div className="modal-backdrop">
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} className="text-danger" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Lead</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this lead? This action cannot be undone and all associated follow-ups will be permanently removed.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn btn-secondary flex-1" onClick={() => setDeleteLeadId(null)}>
                Cancel
              </button>
              <button className="btn flex-1" onClick={confirmDelete} style={{ backgroundColor: 'var(--color-danger)', color: 'white' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadList;
